import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../Navbar/Navbar'; // Assuming Navbar is styled independently
import './BookingSuccess.css'; // This CSS file will be updated
import roundlogo from "./roundlogo.png";
import logo from "./logo.png";

// Ensure your .env file (or environment configuration) has REACT_APP_API_BASE_URL set
const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hitkarihospital.com:5005';


const BookingSuccess = () => {
    const { appointmentId } = useParams();
    const location = useLocation();
    const [receiptData, setReceiptData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [smsSent, setSmsSent] = useState(false);
    const [smsError, setSmsError] = useState(null);

    const paymentResponseFromRazorpay = location.state?.paymentResponse;
    const preloadedAppointmentDetails = location.state?.appointmentDetails;

    const sendAppointmentSms = useCallback(async (details) => {
        if (!details || !details.patient || !details.doctor || !details.appointment) {
            console.warn("[BookingSuccess] Cannot send SMS: Missing crucial details from fetched data.", details);
            setSmsError("Could not gather all details for SMS.");
            return;
        }

        const { patient, doctor, appointment } = details;

        if (!patient.phone) {
            console.warn("[BookingSuccess] Cannot send SMS: Patient phone number is missing.", patient);
            setSmsError("Patient phone number not available for SMS notification.");
            return;
        }

        const smsPayload = {
            mobile: patient.phone,
            patientName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Valued Patient',
            doctorName: doctor.name || 'N/A',
            appointmentDate: appointment.appointmentDate,
            appointmentTime: appointment.appointmentTime,
            appointmentId: appointment._id
        };

        try {
            console.log("[BookingSuccess] Attempting to send appointment confirmation SMS with payload:", smsPayload);
            const response = await axios.post(`${BACKEND_URL}/api/send-appointment-confirmation-sms`, smsPayload);
            if (response.data.success) {
                console.log("[BookingSuccess] Appointment confirmation SMS request successful.");
                setSmsSent(true);
                setSmsError(null);
            } else {
                console.error("[BookingSuccess] Failed to send SMS (API success false):", response.data.message);
                setSmsError(response.data.message || "SMS sending failed (server).");
            }
        } catch (err) {
            console.error("[BookingSuccess] Error sending appointment confirmation SMS (API call failed):", err.response ? err.response.data : err.message);
            setSmsError(err.response?.data?.message || "Error occurred while sending SMS (network/server).");
        }
    }, []); // BACKEND_URL is effectively constant

    useEffect(() => {
        if (appointmentId) {
            setLoading(true);
            axios.get(`${BACKEND_URL}/api/appointments/details/${appointmentId}`)
                .then(response => {
                    if (response.data.success && response.data.data) {
                        setReceiptData(response.data.data);
                        // Send SMS only after successfully fetching full details
                        sendAppointmentSms(response.data.data);
                    } else {
                        setError(response.data.message || "Failed to load complete appointment details.");
                        // If full details fail, but we have preloaded, consider using them (cautiously)
                        if (preloadedAppointmentDetails && preloadedAppointmentDetails.patientPhone) {
                            console.warn("[BookingSuccess] Full details fetch failed, attempting SMS with preloaded data.");
                            const mockReceiptForSms = {
                                patient: {
                                    phone: preloadedAppointmentDetails.patientPhone,
                                    firstName: preloadedAppointmentDetails.patientFirstName,
                                    lastName: preloadedAppointmentDetails.patientLastName
                                },
                                doctor: { name: preloadedAppointmentDetails.doctorName },
                                appointment: {
                                    appointmentDate: preloadedAppointmentDetails.date,
                                    appointmentTime: preloadedAppointmentDetails.time,
                                    appointmentNumber: preloadedAppointmentDetails.appointmentNumber || 'N/A',
                                    _id: appointmentId
                                }
                            };
                            sendAppointmentSms(mockReceiptForSms);
                        }
                    }
                })
                .catch(err => {
                    console.error("Error fetching full appointment data:", err);
                    setError("An error occurred while fetching appointment details.");
                    // Fallback to preloaded details for SMS if fetch fails
                     if (preloadedAppointmentDetails && preloadedAppointmentDetails.patientPhone) {
                        console.warn("[BookingSuccess] Full details fetch errored, attempting SMS with preloaded data.");
                        const mockReceiptForSms = {
                            patient: {
                                phone: preloadedAppointmentDetails.patientPhone,
                                firstName: preloadedAppointmentDetails.patientFirstName,
                                lastName: preloadedAppointmentDetails.patientLastName
                            },
                            doctor: { name: preloadedAppointmentDetails.doctorName },
                            appointment: {
                                appointmentDate: preloadedAppointmentDetails.date,
                                appointmentTime: preloadedAppointmentDetails.time,
                                 appointmentNumber: preloadedAppointmentDetails.appointmentNumber || 'N/A',
                                _id: appointmentId
                            }
                        };
                        sendAppointmentSms(mockReceiptForSms);
                    }
                })
                .finally(() => setLoading(false));
        } else if (preloadedAppointmentDetails) {
            // This case is if appointmentId itself is missing, but we have preloaded details.
            // This is less likely if navigation to this page always includes appointmentId.
            console.warn("[BookingSuccess] No appointmentId, using preloaded details for display and potentially SMS.");
             if (preloadedAppointmentDetails.patientPhone) {
                const mockReceiptForSms = {
                    patient: {
                        phone: preloadedAppointmentDetails.patientPhone,
                        firstName: preloadedAppointmentDetails.patientFirstName,
                        lastName: preloadedAppointmentDetails.patientLastName
                    },
                    doctor: { name: preloadedAppointmentDetails.doctorName },
                    appointment: {
                        appointmentDate: preloadedAppointmentDetails.date,
                        appointmentTime: preloadedAppointmentDetails.time,
                         appointmentNumber: preloadedAppointmentDetails.appointmentNumber || 'N/A',
                        _id: "N/A (preloaded)" // Indicate ID wasn't from URL
                    }
                };
                sendAppointmentSms(mockReceiptForSms);
            }
            setLoading(false); // Not fetching, so set loading to false
        }
        else {
            setError("Appointment ID is missing, and no preloaded details available.");
            setLoading(false);
        }
    }, [appointmentId, preloadedAppointmentDetails, sendAppointmentSms]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="receipt-status-container">
                    <div className="receipt-loader"></div>
                    <p className="receipt-status-message">Loading your booking confirmation...</p>
                </div>
            </>
        );
    }

    if (error && !receiptData && !preloadedAppointmentDetails) { // Only show main error if no data can be displayed
        return (
            <>
                <Navbar />
                <div className="receipt-status-container">
                    <p className="receipt-status-message receipt-error-message">Error: {error}</p>
                    {smsError && <p className="sms-status-message error" style={{marginTop: '10px'}}>SMS Error: {smsError}</p>}
                    <Link to="/" className="receipt-btn receipt-btn-home">Go to Home</Link>
                </div>
            </>
        );
    }

    // Determine which data source to use for rendering
    const displayData = receiptData || (preloadedAppointmentDetails ? {
        appointment: {
            _id: appointmentId || preloadedAppointmentDetails.appointmentId || "N/A", // Use appointmentId from params if available
            doctorFee: preloadedAppointmentDetails.fee,
            appointmentDate: preloadedAppointmentDetails.date,
            appointmentTime: preloadedAppointmentDetails.time,
             appointmentNumber: preloadedAppointmentDetails.appointmentNumber || 'N/A',
            updatedAt: preloadedAppointmentDetails.transactionDate || Date.now()
        },
        patient: {
            firstName: preloadedAppointmentDetails.patientFirstName || "Valued",
            lastName: preloadedAppointmentDetails.patientLastName || "Patient",
            phone: preloadedAppointmentDetails.patientPhone
        },
        doctor: {
            name: preloadedAppointmentDetails.doctorName || "N/A"
        }
    } : null);


    if (!displayData) {
        return (
            <>
                <Navbar />
                <div className="receipt-status-container">
                    <p className="receipt-status-message receipt-warning-message">Could not load booking details.</p>
                    {error && <p className="receipt-status-message receipt-error-message">Details Error: {error}</p>}
                    {smsError && <p className="sms-status-message error" style={{marginTop: '10px'}}>SMS Error: {smsError}</p>}
                    <Link to="/" className="receipt-btn receipt-btn-home">Go to Home</Link>
                </div>
            </>
        );
    }

    const { appointment, patient, doctor } = displayData;

    const amountPaid = paymentResponseFromRazorpay?.amount
        ? paymentResponseFromRazorpay.amount / 100
        : (appointment?.doctorFee);

    return (
        <div className="receipt-main-wrapper">
           <Navbar className="no-print" /> 
            <div className="receipt-header-banner">
                <div className="receipt-container">
                    <h1 className="receipt-banner-title">Booking Confirmation</h1>
                    <p className="receipt-banner-subtitle">Your health journey with us has successfully begun!</p>
                </div>
            </div>

            <div className="receipt-content-area">
                <div className="receipt-container">
                    <div className="receipt-confirmation-card">
                        <div className="receipt-card-header">
                            <img
                                src={logo}
                                alt="Hitkari Hospital Logo"
                                className="receipt-logo"
                            />
                            <div className="receipt-icon-wrapper">
                                <img style={{ height: '65px' }} src={roundlogo} alt="Hitkari Round Logo" />
                            </div>
                            <i className="fas fa-check-circle receipt-success-icon"></i>
                            <h2 className="receipt-status-title">Appointment Successfully Booked!</h2>
                            <p className="receipt-greeting-message">
                                Dear <strong>{patient.firstName} {patient.lastName}</strong>, your appointment details are below.
                            </p>
                            {/* Display SMS status */}
                            {/* {smsSent && <p className="sms-status-message success">✓ Confirmation SMS sent to {patient.phone}.</p>} */}
                            {smsError && <p className="sms-status-message error">! SMS could not be sent: {smsError}</p>}
                            {/* Display main fetch error if it occurred but we still have preloaded data */}
                            {error && receiptData === null && preloadedAppointmentDetails &&
                                <p className="sms-status-message warning">Note: Displaying preliminary details. Full confirmation might be pending. Error: {error}</p>
                            }
                        </div>

                        <div className="receipt-section receipt-highlight-section receipt-appointment-summary">
                            <h3 className="receipt-section-title">
                                <i className="fas fa-calendar-check"></i> Appointment Summary
                            </h3>
                            <div className="receipt-detail-grid">
                                <div className="receipt-detail-item prominent">
                                    <span className="receipt-detail-label">Doctor:</span>
                                    <span className="receipt-detail-value"> {doctor.name}</span>
                                </div>
                                <div className="receipt-detail-item prominent">
                                    <span className="receipt-detail-label">Date & Time:</span>
                                    <span className="receipt-detail-value">{appointment.appointmentDate} at {appointment.appointmentTime}</span>
                                </div>
                                {appointment.appointmentNumber && appointment.appointmentNumber !== "N/A (preloaded)" && (
                                    <div className="receipt-detail-item">
                                        <span className="receipt-detail-label">Appointment ID:</span>
                                        <span className="receipt-detail-value receipt-booking-id">{appointment.appointmentNumber}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="receipt-info-columns">
                            <div className="receipt-section receipt-payment-details">
                                <h3 className="receipt-section-title">
                                    <i className="fas fa-credit-card"></i> Payment Details
                                </h3>
                                <div className="receipt-detail-item">
                                    <span className="receipt-detail-label">Amount Paid:</span>
                                    <span className="receipt-detail-value receipt-amount-paid">₹{amountPaid?.toFixed(2) || 'N/A'}</span>
                                </div>
                                {paymentResponseFromRazorpay?.razorpay_payment_id && (
                                    <div className="receipt-detail-item">
                                        <span className="receipt-detail-label">Payment ID:</span>
                                        <span className="receipt-detail-value">{paymentResponseFromRazorpay.razorpay_payment_id}</span>
                                    </div>
                                )}
                                {paymentResponseFromRazorpay?.razorpay_order_id && (
                                    <div className="receipt-detail-item">
                                        <span className="receipt-detail-label">Order ID:</span>
                                        <span className="receipt-detail-value">{paymentResponseFromRazorpay.razorpay_order_id}</span>
                                    </div>
                                )}
                                <div className="receipt-detail-item">
                                    <span className="receipt-detail-label">Transaction Date:</span>
                                    <span className="receipt-detail-value">{new Date(appointment.updatedAt || Date.now()).toLocaleString()}</span>
                                </div>
                            </div>

                            <div className="receipt-section receipt-patient-info">
                                <h3 className="receipt-section-title">
                                    <i className="fas fa-user-circle"></i> Patient Information
                                </h3>
                                <div className="receipt-detail-item">
                                    <span className="receipt-detail-label">Name:</span>
                                    <span className="receipt-detail-value" style={{ fontWeight: "900" }}>{patient.firstName} {patient.lastName}</span>
                                </div>
                                {patient.phone && (
                                    <div className="receipt-detail-item">
                                        <span className="receipt-detail-label">Phone:</span>
                                        <span className="receipt-detail-value">{patient.phone}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="receipt-actions">
                            <button onClick={handlePrint} className="receipt-btn receipt-btn-primary">
                                <i className="fas fa-print"></i> Print Receipt
                            </button>
                            <Link to="/" className="receipt-btn receipt-btn-secondary">
                                <i className="fas fa-home"></i> Go to Homepage
                            </Link>
                        </div>

                        <div className="receipt-footer-note">
                            <p><i className="fas fa-info-circle"></i> {smsSent ? "A confirmation SMS has been sent." : "An SMS confirmation will be sent if your phone number is on record."} Please check your inbox (and spam folder for emails if applicable). If you have any questions or need to reschedule, please contact our support team. We look forward to assisting you!</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingSuccess;